// src/store/index.js - État global avec Zustand
import { create } from 'zustand';
import { authAPI, paymentAPI, messageAPI } from '../services/api';
import E2E from '../services/e2eService';
import { socketService } from '../services/socket';
import toast from 'react-hot-toast';

// ============================================
// STORE AUTH
// ============================================
const decodeJwtExp = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1])).exp;
  } catch {
    return null;
  }
};

export const useAuthStore = create((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,

  initialize: async () => {
    const clearSession = () => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      set({ user: null, isAuthenticated: false, isInitialized: true });
    };

    try {
      const token = localStorage.getItem('access_token');
      const rawUser = localStorage.getItem('user');
      if (!token || !rawUser) {
        set({ isInitialized: true });
        return;
      }

      let parsedUser;
      try {
        parsedUser = JSON.parse(rawUser);
      } catch {
        return clearSession();
      }

      const exp = decodeJwtExp(token);
      const isExpired = !exp || exp * 1000 <= Date.now();

      if (!isExpired) {
        set({ user: parsedUser, isAuthenticated: true, isInitialized: true });
        socketService.connect(token);
        return;
      }

      // Access token expiré -> tenter un refresh silencieux avant de rejeter la session
      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (!refreshTokenValue) return clearSession();

      const res = await authAPI.refresh({ refresh_token: refreshTokenValue });
      const newAccessToken = res.data?.access_token;
      if (!newAccessToken) return clearSession();

      localStorage.setItem('access_token', newAccessToken);
      set({ user: parsedUser, isAuthenticated: true, isInitialized: true });
      socketService.connect(newAccessToken);
    } catch {
      clearSession();
    }
  },

  login: async (credentials) => {
    set({ isLoading: true });
    try {
      const res = await authAPI.login(credentials);
      if (res.requires2FA || res.data?.requires2FA) {
        return { requires2FA: true, tempToken: res.tempToken || res.data?.tempToken };
      }
      const { access_token, refresh_token, user } = res.data || res;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));
      set({ user, isAuthenticated: true });
      socketService.connect(access_token);
      // Initialiser E2E
      try {
        const { publicKey } = await E2E.init(user.id);
        const token = access_token;
        fetch('/api/profile/public-key', {
          method: 'PUT',
          headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
          body: JSON.stringify({ public_key: publicKey })
        });
      } catch(e) { console.warn('E2E init failed:', e); }
      toast.success(`Bienvenue ${user.display_name} ! 👋`);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.error };
    } finally {
      set({ isLoading: false });
    }
  },

  logout: async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.clear();
    socketService.disconnect();
    set({ user: null, isAuthenticated: false });
    window.location.href = '/login';
  },
}));

// ============================================
// STORE CONVERSATIONS & MESSAGES
// ============================================
export const useMessageStore = create((set, get) => ({
  conversations: [],
  activeConversationId: null,
  messages: {}, // { conversationId: [msg, ...] }
  typingUsers: {}, // { conversationId: { userId: name } }
  onlineUsers: {}, // { userId: true/false }
  isLoading: false,

  fetchConversations: async () => {
    try {
      const res = await messageAPI.getConversations();
      set({ conversations: res.data });
    } catch {}
  },

  setActiveConversation: async (conversationId) => {
    set({ activeConversationId: conversationId });
    const state = get();
    if (!state.messages[conversationId]) {
      await state.fetchMessages(conversationId);
    }
    // Rejoindre la room Socket.io
    socketService.joinConversation(conversationId);
    // Marquer tous les messages comme lus
    const msgs = get().messages[conversationId] || [];
    const lastMsg = msgs[msgs.length - 1];
    if (lastMsg) socketService.markRead(conversationId, lastMsg.id);
  },

  fetchMessages: async (conversationId) => {
    set({ isLoading: true });
    try {
      const res = await messageAPI.getMessages(conversationId);
      set(state => ({
        messages: { ...state.messages, [conversationId]: res.data }
      }));
    } catch {} finally {
      set({ isLoading: false });
    }
  },

  updateMessage: (conversationId, messageId, updates) => {
    set(state => ({
      messages: {
        ...state.messages,
        [conversationId]: (state.messages[conversationId] || []).map(m =>
          m.id === messageId ? { ...m, ...updates } : m
        ),
      }
    }));
  },

  addMessage: (message) => {
    const convId = message.conversation_id;
    // Si la conversation est active, marquer comme lu
    if (get().activeConversationId === convId && message.sender_id !== useAuthStore.getState().user?.id) {
      socketService.markRead(convId, message.id);
    }
    set(state => {
      const existing = state.messages[convId] || [];
      if (existing.some(m => m.id === message.id)) return state;
      const filtered = existing.filter(m => !m.isTemp);
      return {
        messages: {
          ...state.messages,
          [convId]: [...filtered, message],
        },
        conversations: state.conversations.map(c => {
          if (c.id !== convId) return c;
          const isActive = state.activeConversationId === convId;
          const fromMe = message.sender_id === useAuthStore.getState().user?.id;
          const newUnread = (isActive || fromMe) ? 0 : (c.unread_count || 0) + 1;
          return {
            ...c,
            last_message_content: message.content,
            last_message_at: message.created_at,
            updated_at: message.created_at,
            unread_count: newUnread,
          };
        }).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
      };
    });
  },

  sendMessage: async (conversationId, content, contactId) => {
    // Optimistic update (afficher immédiatement)
    const user = useAuthStore.getState().user;
    const tempMsg = {
      id: `temp-${Date.now()}`,
      conversation_id: conversationId,
      sender_id: user.id,
      sender_name: user.display_name,
      type: 'text',
      content,
      created_at: new Date().toISOString(),
      isTemp: true,
    };
    get().addMessage(tempMsg);

    try {
      // Chiffrer le message E2E si possible
      let encryptedContent = content;
      let isEncrypted = false;
      if (contactId) {
        try {
          const token = localStorage.getItem('access_token');
          const pkRes = await fetch('/api/users/' + contactId + '/public-key', {
            headers: { Authorization: 'Bearer ' + token }
          });
          const pkData = await pkRes.json();
          if (pkData.success && pkData.public_key) {
            encryptedContent = await E2E.encrypt(content, pkData.public_key);
            isEncrypted = true;
          }
        } catch(e) { console.warn('E2E skip:', e.message); }
      }
      // Essayer WebSocket d'abord
      if (socketService.isConnected()) {
        socketService.sendMessage(conversationId, encryptedContent, (response) => {
          if (response.success) {
            set(state => ({
              messages: {
                ...state.messages,
                [conversationId]: state.messages[conversationId]?.map(m =>
                  m.id === tempMsg.id ? response.data : m
                ) || [],
              }
            }));
          }
        });
      } else {
        // Fallback REST si WebSocket non connecté
        const response = await messageAPI.sendMessage(conversationId, { content: encryptedContent, is_encrypted: isEncrypted });
        if (response.success) {
          set(state => ({
            messages: {
              ...state.messages,
              [conversationId]: state.messages[conversationId]?.map(m =>
                m.id === tempMsg.id ? response.data : m
              ) || [],
            }
          }));
        }
      }
    } catch(err) {
      console.error('Erreur envoi message:', err);
    }
  },

  setTyping: (conversationId, userId, userName, isTyping) => {
    set(state => {
      const conv = { ...(state.typingUsers[conversationId] || {}) };
      if (isTyping) conv[userId] = userName;
      else delete conv[userId];
      return { typingUsers: { ...state.typingUsers, [conversationId]: conv } };
    });
  },

  setOnlineStatus: (userId, online) => {
    set(state => ({
      onlineUsers: { ...state.onlineUsers, [userId]: online }
    }));
  },

  markConversationRead: (conversationId) => {
    set(state => ({
      conversations: state.conversations.map(c =>
        c.id === conversationId ? { ...c, unread_count: 0 } : c
      )
    }));
  },

  createConversation: async (participantIds, type = 'direct', name = null) => {
    try {
      const res = await messageAPI.createConversation({
        type, participant_ids: participantIds, name,
      });
      await get().fetchConversations();
      return res.data;
    } catch (err) {
      toast.error('Impossible de créer la conversation');
      return null;
    }
  },
}));

// ============================================
// STORE WALLET & PAIEMENTS
// ============================================
export const usePaymentStore = create((set, get) => ({
  wallet: null,
  transactions: [],
  isLoading: false,

  fetchWallet: async () => {
    try {
      const res = await paymentAPI.getWallet();
      set({ wallet: res.data });
    } catch {}
  },

  fetchTransactions: async (params = {}) => {
    set({ isLoading: true });
    try {
      const res = await paymentAPI.getTransactions(params);
      set({ transactions: res.data.transactions });
    } catch {} finally {
      set({ isLoading: false });
    }
  },

  sendPayment: async ({ receiverId, amount, note, conversationId, pin }) => {
    set({ isLoading: true });
    try {
      const res = await paymentAPI.sendPayment({
        receiver_id: receiverId,
        amount: parseFloat(amount),
        note,
        conversation_id: conversationId,
        pin,
      });
      // Rafraîchir le solde
      await get().fetchWallet();
      toast.success(`💸 ${amount}FCFA  envoyé avec succès !`);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.error };
    } finally {
      set({ isLoading: false });
    }
  },

  requestPayment: async ({ fromUserId, amount, note, conversationId }) => {
    try {
      const res = await paymentAPI.requestPayment({
        from_user_id: fromUserId,
        amount: parseFloat(amount),
        note,
        conversation_id: conversationId,
      });
      toast.success(`📥 Demande de ${amount}FCFA  envoyée`);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.error };
    }
  },

  splitBill: async ({ conversationId, totalAmount, title, participantIds }) => {
    try {
      const res = await paymentAPI.splitBill({
        conversation_id: conversationId,
        total_amount: parseFloat(totalAmount),
        title,
        participant_ids: participantIds,
      });
      toast.success(`⚡ Facture partagée entre ${participantIds.length + 1} personnes`);
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.error };
    }
  },
}));
